import { memo, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  Table,
  Button,
  Tooltip,
  Spin,
  Empty,
  Input,
  DatePicker,
  Space,
  Switch,
  message,
} from 'antd';
import {
  ReloadOutlined,
  ExpandOutlined,
  CompressOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  UserOutlined,
  DownloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useAtom, useAtomValue } from 'jotai';
import { currentProjectAtom } from '~/stores/ProjectStore';
import { showFreePhotoVectorTilesAtom } from '~/components/Map/stores/mapStore';
import { remote_CollectRestoreTransaction_freePhoto } from '~/common/typescript/apis/CollectSyncTransactionAPIs';
import type { FreePhoto_Base } from '~/common/typescript/models/FreePhoto';
import { parseMetadata, getLocationText } from '~/screens/FreePhotoScreen/utils/freePhotoUtils';
import { LazyImage } from '~/screens/FreePhotoScreen/components/LazyImage';
import { useFreePhotoDownload } from '~/screens/FreePhotoScreen/hooks/useFreePhotoDownload';
import { useFreePhotoMetadata } from '~/screens/FreePhotoScreen/hooks/useFreePhotoMetadata';
import { useUserById } from '~/screens/FreePhotoScreen/hooks/useUserById';
import { useScreenSize } from '~/hooks/useScreenSize';
import DateTimeViewer from '~/components/common/DateTimeViewer';
import FreePhotoDetailModal from './FreePhotoDetailModal';
import { showFreePhotoPopup } from '~/components/Map/controllers/popups/FreePhotoPopup';

const { RangePicker } = DatePicker;
const { Search } = Input;

/* ── Sub-components (cần hook → tách riêng) ───────────────────────────────── */

const LocationCell = ({ photo }: { photo: FreePhoto_Base }) => {
  const meta = useFreePhotoMetadata(photo);
  const loc = getLocationText(meta);
  return (
    <UI_Location title={loc}>
      <EnvironmentOutlined style={{ flexShrink: 0, marginRight: 4, color: '#3b82f6' }} />
      <span>{loc}</span>
    </UI_Location>
  );
};

const UserCell = ({ userId }: { userId?: number }) => {
  const { user } = useUserById(userId);
  if (!userId) return <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>;
  const name = user ? (user.fullname || user.username) : null;
  return (
    <UI_UserCell>
      <UserOutlined style={{ color: '#64748b', fontSize: 11 }} />
      <span>{name ?? <span style={{ color: '#94a3b8' }}>#{userId}</span>}</span>
    </UI_UserCell>
  );
};

/* ─────────────────────────────────────────────────────────────────────────── */

interface FreePhotoTabProps {
  tableHeight?: string;
  onMaximize?: () => void;
  onClose?: () => void;
  isMaximized?: boolean;
}

const FreePhotoTab = ({ tableHeight, onMaximize, onClose, isMaximized }: FreePhotoTabProps) => {
  const currentProject = useAtomValue(currentProjectAtom);
  const { isMobile } = useScreenSize();
  const { downloadPhoto, downloading } = useFreePhotoDownload();

  // Vector tile toggle — controls FreePhotoVectorTileController visibility
  const [showOnMap, setShowOnMap] = useAtom(showFreePhotoVectorTilesAtom);

  const [photos, setPhotos] = useState<FreePhoto_Base[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });

  // Filters
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);

  // Detail modal
  const [selectedPhoto, setSelectedPhoto] = useState<FreePhoto_Base | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  const fetchPhotos = useCallback(async (page = 1, pageSize = 10) => {
    if (!currentProject?.id) return;
    setLoading(true);
    try {
      const response = await remote_CollectRestoreTransaction_freePhoto({
        projectId: currentProject.id,
        page,
        pageSize,
      });

      let results = response.results || [];

      // Client-side search filter
      if (searchText.trim()) {
        const query = searchText.trim().toLowerCase();
        results = results.filter((photo) => {
          const meta = parseMetadata(photo.metadata);
          const loc = getLocationText(meta);
          return (
            photo.file_name.toLowerCase().includes(query) ||
            loc.toLowerCase().includes(query)
          );
        });
      }

      // Client-side date range filter
      if (dateRange) {
        const [start, end] = dateRange;
        results = results.filter((photo) => {
          if (!photo.created_at) return false;
          const d = new Date(photo.created_at);
          return d >= new Date(start) && d <= new Date(end + 'T23:59:59');
        });
      }

      setPhotos(results);
      setPagination((prev) => ({
        ...prev,
        page,
        pageSize,
        total: response.pagination?.total ?? results.length,
      }));
    } catch (err) {
      console.error('Failed to fetch free photos:', err);
      message.error('Không thể tải danh sách ảnh tự do');
    } finally {
      setLoading(false);
    }
  }, [currentProject?.id, searchText, dateRange]);

  useEffect(() => {
    fetchPhotos(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject?.id, searchText, dateRange]);

  const handleRowClick = (photo: FreePhoto_Base) => {
    const meta = parseMetadata(photo.metadata);
    const lat = meta?.location?.coords?.latitude;
    const lng = meta?.location?.coords?.longitude;
    if (lat != null && lng != null) {
      const map = window.map;
      if (map) {
        map.flyTo({ center: [lng, lat], zoom: 16, essential: true, duration: 1000 });
      }
      showFreePhotoPopup({
        title: photo.file_name,
        lngLat: { lng, lat },
        properties: {
          uuid: photo.uuid,
          file_name: photo.file_name,
          created_at: photo.created_at,
          compass_heading: (meta?.compassHeading as any)?.trueHeading ?? meta?.compassHeading,
          user_id: photo.user_id,
          project_id: photo.project_id,
        },
      });
    }
  };

  const handleViewDetail = (photo: FreePhoto_Base) => {
    setSelectedPhoto(photo);
    setDetailModalVisible(true);
  };

  const handleDownload = (photo: FreePhoto_Base) => {
    downloadPhoto(photo.uuid, photo.file_name);
  };

  const handleNavigatePhoto = useCallback(
    (direction: 'prev' | 'next') => {
      if (!selectedPhoto || photos.length === 0) return;
      const idx = photos.findIndex((p) => p.uuid === selectedPhoto.uuid);
      if (idx === -1) return;
      let next = direction === 'prev' ? idx - 1 : idx + 1;
      if (next < 0) next = photos.length - 1;
      if (next >= photos.length) next = 0;
      setSelectedPhoto(photos[next]);
    },
    [selectedPhoto, photos],
  );

  const columns = [
    {
      title: 'Ảnh',
      key: 'thumbnail',
      width: 72,
      render: (_: any, record: FreePhoto_Base) => (
        <UI_ThumbWrapper>
          <LazyImage
            uuid={record.uuid}
            ossProcess="image/resize,w_300"
            alt={record.file_name}
            width={60}
            height={60}
          />
        </UI_ThumbWrapper>
      ),
    },
    {
      title: 'Vị trí',
      key: 'location',
      render: (_: any, record: FreePhoto_Base) => <LocationCell photo={record} />,
    },
    {
      title: 'Người chụp',
      key: 'user',
      width: 130,
      render: (_: any, record: FreePhoto_Base) => <UserCell userId={record.user_id} />,
    },
    {
      title: 'Ngày chụp',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date: string) => (
        <DateTimeViewer date={date} initialMode="relative" severity="secondary" showTooltip />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 68,
      render: (_: any, record: FreePhoto_Base) => (
        <Space size={2} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Xem chi tiết">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>
          <Tooltip title="Tải xuống">
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              loading={downloading}
              onClick={() => handleDownload(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Container>
      {/* Toolbar */}
      <ControlRow>
        <RecordCount>
          {photos.length} ảnh
          {currentProject?.id ? ` (Dự án #${currentProject.id})` : ''}
        </RecordCount>
        <ControlActions>
          <Tooltip title="Làm mới">
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined spin={loading} />}
              onClick={() => fetchPhotos(1, pagination.pageSize)}
            />
          </Tooltip>
          {onMaximize && (
            <Tooltip title={isMaximized ? 'Thu nhỏ' : 'Phóng to'}>
              <Button
                type="text"
                size="small"
                icon={isMaximized ? <CompressOutlined /> : <ExpandOutlined />}
                onClick={onMaximize}
              />
            </Tooltip>
          )}
          {onClose && (
            <Tooltip title="Đóng">
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={onClose}
              />
            </Tooltip>
          )}
        </ControlActions>
      </ControlRow>

      {/* Filters */}
      <FilterRow>
        <Search
          placeholder="Tìm theo tên file, địa điểm..."
          allowClear
          size="small"
          style={{ flex: 1, minWidth: 120, maxWidth: 240 }}
          onSearch={(v) => setSearchText(v)}
          onChange={(e) => !e.target.value && setSearchText('')}
        />
        <RangePicker
          size="small"
          style={{ flex: 1, minWidth: 200 }}
          onChange={(_, strs) => setDateRange(strs[0] && strs[1] ? [strs[0], strs[1]] : null)}
        />
        <UI_MapToggle>
          <span style={{ fontSize: 11, color: '#64748b' }}>Bản đồ</span>
          <Switch
            size="small"
            checked={showOnMap}
            onChange={setShowOnMap}
          />
        </UI_MapToggle>
      </FilterRow>

      {/* Vector tile toggle — FreePhotoVectorTileController in MapScreen reacts to showFreePhotoVectorTilesAtom */}

      {/* Table */}
      <Spin spinning={loading}>
        {!loading && photos.length === 0 ? (
          <UI_Empty>
            <Empty
              description="Không có ảnh tự do nào"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </UI_Empty>
        ) : (
          <Table
            columns={columns}
            dataSource={photos}
            rowKey="uuid"
            size="small"
            bordered
            scroll={{ y: tableHeight ? `calc(${tableHeight} - 100px)` : 300 }}
            pagination={{
              current: pagination.page,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: !isMobile,
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
              onChange: (page, pageSize) => fetchPhotos(page, pageSize),
            }}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: 'pointer' },
            })}
          />
        )}
      </Spin>

      {/* Detail modal */}
      <FreePhotoDetailModal
        visible={detailModalVisible}
        photo={selectedPhoto}
        photos={photos}
        onClose={() => {
          setDetailModalVisible(false);
          setSelectedPhoto(null);
        }}
        onNavigate={handleNavigatePhoto}
      />
    </Container>
  );
};

export default memo(FreePhotoTab);

/* ─── Styled Components ─────────────────────────────────────────────────────── */

const Container = styled.div`
  padding: 6px;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow: hidden;

  .ant-table {
    font-size: 12px;
  }

  .ant-table-thead > tr > th {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    font-weight: 600;
    font-size: 10px !important;
    color: #374151;
    border-bottom: 2px solid #e2e8f0;
    padding: 4px !important;
  }

  .ant-table-tbody > tr > td {
    padding: 4px !important;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
  }

  .ant-table-tbody > tr {
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .ant-table-tbody > tr:hover > td {
    background: rgba(59, 130, 246, 0.05) !important;
  }
`;

const ControlRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 4px 0;
`;

const RecordCount = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #374151;
`;

const ControlActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const UI_ThumbWrapper = styled.div`
  width: 60px;
  height: 60px;
  overflow: hidden;
  border-radius: 4px;
  flex-shrink: 0;
`;

const UI_Location = styled.div`
  font-size: 11px;
  color: #374151;
  display: flex;
  align-items: flex-start;
  gap: 2px;
  line-height: 1.4;

  span {
    word-break: break-word;
    white-space: normal;
  }
`;

const UI_UserCell = styled.div`
  font-size: 11px;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UI_MapToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  height: 24px;
`;

const UI_Empty = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
`;
